import { useChangePasswordMutation } from '@entities/Authentication/rtk-query.ts';
import { resetPasswordSchema } from '@entities/Authentication/schemas.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { useState } from 'react';
import { useForm, useWatch, type SubmitHandler } from 'react-hook-form';
import { useAppDispatch } from '@shared/hooks/index.ts';
import { CommonButton } from '@shared/ui/index.ts';
import { closeAuthModal, showPopup } from '@src/library/redux/modal/index.ts';
import ButtonLink from '@src/modules/Authentication/ButtonLink/index.ts';
import CustomInput from '@src/modules/Authentication/CustomInput/index.ts';
import { ModalError } from '@src/modules/Authentication/frames/modal-error';
import { PasswordHint } from '@src/modules/Authentication/frames/password-hint';
import * as formStyles from '../../AuthFrom/styles.module.scss';
import * as st from './styles.module.scss';

type ResetPasswordForm = {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
};
type PT = {
  openRestore: () => void;
};

export const ResetPassword = ({ openRestore }: PT) => {
  const { control, handleSubmit, formState, setError } = useForm<ResetPasswordForm>({
    resolver: yupResolver(resetPasswordSchema),
  });

  const old_password = useWatch({ name: 'old_password', control });
  const new_password = useWatch({ name: 'new_password', control });
  const new_password_confirm = useWatch({ name: 'new_password_confirm', control });
  const isAllFieldsFulfilled = old_password && new_password && new_password_confirm;

  const dispatch = useAppDispatch();

  const [isCurrentPasswordViewed, setIsCurrentPasswordViewed] = useState(false);
  const [isNewPasswordViewed, setIsNewPasswordViewed] = useState(false);
  const [isNewPasswordConfirmViewed, setIsNewPasswordConfirmViewed] = useState(false);
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const onSubmit: SubmitHandler<ResetPasswordForm> = async (data) => {
    const { old_password, new_password } = data;
    const res = await changePassword({
      old_password,
      new_password,
    });

    const response = res as {
      error?: { data?: { error?: { old_password?: string; new_password?: string } } };
    };

    if (response?.error) {
      const oldPasswordError = response?.error?.data?.error?.old_password;
      const newPasswordError = response?.error?.data?.error?.new_password;
      if (oldPasswordError) {
        setError('old_password', { type: 'value', message: oldPasswordError });
      }
      if (newPasswordError) {
        setError('new_password', { type: 'value', message: newPasswordError });
        setError('new_password_confirm', { type: 'value', message: newPasswordError });
      }
    } else {
      dispatch(showPopup({ type: 'success', text: 'Пароль успешно изменён' }));
      dispatch(closeAuthModal());
    }
  };

  const formStateErrors = formState.errors;

  const errorMessage =
    formStateErrors?.old_password?.message ||
    formStateErrors?.new_password?.message ||
    formStateErrors?.new_password_confirm?.message;

  return (
    <form
      className={cn(formStyles.form, st.form)}
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      <div>
        <section className={st.currentPasswordBlock}>
          <CustomInput
            tabindex={0}
            type="password"
            label="Старый пароль"
            name="old_password"
            control={control}
            error={formState.errors.old_password}
            disabled={isLoading}
            showPassword={isCurrentPasswordViewed}
            setValue={() => {
              setIsCurrentPasswordViewed((isViewed) => !isViewed);
            }}
            autoComplete={false}
          />

          <ButtonLink className={st.forgotPassword} disabled={isLoading} handler={openRestore}>
            Забыли пароль?
          </ButtonLink>
        </section>

        <CustomInput
          wrapperClassName={st.newPasswordField}
          tabindex={1}
          type="password"
          label="Новый пароль"
          name="new_password"
          control={control}
          error={formState.errors.new_password}
          disabled={isLoading}
          showPassword={isNewPasswordViewed}
          setValue={() => {
            setIsNewPasswordViewed((isViewed) => !isViewed);
          }}
          autoComplete={false}
        />

        <CustomInput
          tabindex={2}
          type="password"
          label="Подтвердите пароль"
          name="new_password_confirm"
          control={control}
          error={formState.errors.new_password_confirm}
          disabled={isLoading}
          showPassword={isNewPasswordConfirmViewed}
          setValue={() => {
            setIsNewPasswordConfirmViewed((isViewed) => !isViewed);
          }}
          autoComplete={false}
        />

        <PasswordHint className={st.passwordHint} />
      </div>

      {errorMessage ? (
        <ModalError error={errorMessage} />
      ) : (
        <CommonButton tabIndex={3} type="submit" disabled={isLoading || !isAllFieldsFulfilled}>
          Готово
        </CommonButton>
      )}
    </form>
  );
};
